/**
 * ImportVendorsDialog — the "Import CSV" flow for the vendors list. Simple
 * bulk-create: pick a CSV, preview per-row validity (validated against the
 * same `vendors.createInput` the server uses), then submit the ready rows
 * through `vendors.import`. Duplicate GSTINs already on file are skipped by
 * the server and reported back in the result summary.
 *
 * @see features/vendors/import/map-import-rows.ts — parsing + validation
 */
import { useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, Download, FileUp, Upload, X } from "lucide-react";
import { DialogRoot, DialogContent, cn, toast } from "@flint/ui";
import type { vendors as vendorsContract } from "@flint/api-contract";
import { trpc } from "../../../lib/trpc.ts";
import {
  buildImportPreview,
  importTemplateCsv,
  type ImportPreview,
} from "./map-import-rows.ts";

export interface ImportVendorsDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
}

type ImportResult = vendorsContract.ImportOutput;

export function ImportVendorsDialog({ open, onOpenChange }: ImportVendorsDialogProps) {
  const utils = trpc.useUtils();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [fileName, setFileName] = useState<string | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);

  const importMutation = trpc.vendors.import.useMutation({
    onSuccess: async (data) => {
      setResult(data);
      await Promise.all([utils.vendors.list.invalidate(), utils.vendors.counts.invalidate()]);
      if (data.created > 0) {
        toast.success(`Imported ${data.created} vendor${data.created === 1 ? "" : "s"}.`);
      }
    },
    onError: (err) => {
      setParseError(err.message);
    },
  });

  function reset() {
    setFileName(null);
    setPreview(null);
    setParseError(null);
    setResult(null);
    importMutation.reset();
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  function handleClose(next: boolean) {
    if (!next) reset();
    onOpenChange(next);
  }

  async function handleFile(file: File) {
    setParseError(null);
    setResult(null);
    setFileName(file.name);
    try {
      const text = await file.text();
      const built = buildImportPreview(text);
      setPreview(built);
      if (built.headerError) setParseError(built.headerError);
    } catch {
      setParseError("Couldn't read that file. Make sure it's a plain-text CSV.");
      setPreview(null);
    }
  }

  function downloadTemplate() {
    const blob = new Blob([importTemplateCsv()], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "vendor-import-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  function handleImport() {
    if (!preview) return;
    const rows = preview.rows.filter((r) => r.status === "ready" && r.payload).map((r) => r.payload!);
    if (rows.length === 0) return;
    importMutation.mutate({ rows });
  }

  const canImport = (preview?.readyCount ?? 0) > 0 && !importMutation.isPending && !result;

  return (
    <DialogRoot open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <div className="flex flex-col gap-4">
          <header className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-base font-semibold text-ink m-0">Import vendors from CSV</h2>
              <p className="text-xs text-muted mt-1 leading-snug">
                Bulk-add suppliers. Each row is validated before import; duplicates of vendors
                already on file are skipped.
              </p>
            </div>
            <button
              type="button"
              onClick={() => handleClose(false)}
              className="text-muted hover:text-ink shrink-0"
              aria-label="Close"
            >
              <X size={18} strokeWidth={2} aria-hidden />
            </button>
          </header>

          {/* ── Result view ─────────────────────────────────────────── */}
          {result ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-ink">
                <CheckCircle2 size={18} strokeWidth={2} aria-hidden className="text-ok" />
                <span className="text-sm font-semibold">Import complete</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <ResultTile label="Created" value={result.created} tone="ok" />
                <ResultTile label="Skipped" value={result.skipped} tone="warn" />
                <ResultTile label="Failed" value={result.failed} tone={result.failed ? "bad" : "muted"} />
              </div>
              {result.skipped > 0 ? (
                <p className="text-xs text-muted">
                  Skipped rows had a GSTIN that already belongs to an existing vendor.
                </p>
              ) : null}
              <div className="flex justify-end gap-2 pt-2 border-t border-line">
                <button
                  type="button"
                  onClick={reset}
                  className="h-9 px-3 rounded-md text-sm text-ink-2 hover:bg-bg"
                >
                  Import another file
                </button>
                <button
                  type="button"
                  onClick={() => handleClose(false)}
                  className="h-9 px-4 rounded-md bg-ink text-paper text-sm font-semibold hover:bg-ink-2"
                >
                  Done
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* ── File picker ───────────────────────────────────────── */}
              <div className="flex items-center gap-3 flex-wrap">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) void handleFile(f);
                  }}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center gap-2 h-9 px-3.5 rounded-sm border border-line bg-paper text-sm font-semibold text-ink hover:bg-bg"
                >
                  <FileUp size={14} strokeWidth={2} aria-hidden />
                  {fileName ? "Choose a different file" : "Choose CSV file"}
                </button>
                {fileName ? <span className="text-xs text-muted truncate">{fileName}</span> : null}
                <button
                  type="button"
                  onClick={downloadTemplate}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-sm text-xs font-semibold text-sky-700 hover:bg-sky-50 ml-auto"
                >
                  <Download size={12} strokeWidth={2} aria-hidden />
                  Download template
                </button>
              </div>

              {!preview && !parseError ? (
                <p className="text-xs text-muted leading-relaxed">
                  Expected columns: <span className="font-medium text-ink-2">Name</span> (required),
                  GSTIN, Contact, Phone, Email, Address 1, City, State, PIN, Payment terms, TDS
                  section, Job worker. Extra columns are ignored. The first row must be a header.
                </p>
              ) : null}

              {parseError ? (
                <div className="flex items-start gap-2 rounded-sm border border-bad/30 bg-bad/5 px-3 py-2 text-xs text-bad">
                  <AlertTriangle size={14} strokeWidth={2} aria-hidden className="mt-0.5 shrink-0" />
                  <span>{parseError}</span>
                </div>
              ) : null}

              {/* ── Preview ───────────────────────────────────────────── */}
              {preview && !preview.headerError ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-3 gap-2">
                    <ResultTile label="Ready" value={preview.readyCount} tone="ok" />
                    <ResultTile label="Duplicate" value={preview.duplicateCount} tone="warn" />
                    <ResultTile label="Has errors" value={preview.errorCount} tone={preview.errorCount ? "bad" : "muted"} />
                  </div>

                  <div className="max-h-[280px] overflow-auto rounded-sm border border-line">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-bg">
                        <tr className="text-left text-muted">
                          <th className="px-2.5 py-1.5 font-medium w-10">#</th>
                          <th className="px-2.5 py-1.5 font-medium">Vendor</th>
                          <th className="px-2.5 py-1.5 font-medium w-24">Status</th>
                          <th className="px-2.5 py-1.5 font-medium">Detail</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.rows.map((r) => (
                          <tr key={r.line} className="border-t border-line/60">
                            <td className="px-2.5 py-1.5 text-muted tabular-nums">{r.line}</td>
                            <td className="px-2.5 py-1.5 text-ink truncate max-w-[180px]">{r.name}</td>
                            <td className="px-2.5 py-1.5">
                              <StatusPill status={r.status} />
                            </td>
                            <td className="px-2.5 py-1.5 text-muted">{r.message ?? "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}

              {/* ── Footer ────────────────────────────────────────────── */}
              <div className="flex items-center justify-end gap-2 pt-2 border-t border-line">
                <button
                  type="button"
                  onClick={() => handleClose(false)}
                  className="h-9 px-3 rounded-md text-sm text-ink-2 hover:bg-bg"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleImport}
                  disabled={!canImport}
                  className={cn(
                    "inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-ink text-paper text-sm font-semibold",
                    "hover:bg-ink-2 disabled:opacity-50 disabled:cursor-not-allowed",
                  )}
                >
                  <Upload size={13} strokeWidth={2.2} aria-hidden />
                  {importMutation.isPending
                    ? "Importing…"
                    : preview?.readyCount
                      ? `Import ${preview.readyCount} vendor${preview.readyCount === 1 ? "" : "s"}`
                      : "Import"}
                </button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </DialogRoot>
  );
}

function ResultTile({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: "ok" | "warn" | "bad" | "muted";
}) {
  const toneClass =
    tone === "ok"
      ? "text-ok"
      : tone === "warn"
        ? "text-warn"
        : tone === "bad"
          ? "text-bad"
          : "text-ink";
  return (
    <div className="rounded-sm border border-line bg-bg px-3 py-2">
      <div className={cn("text-lg font-bold tabular-nums", toneClass)}>{value}</div>
      <div className="text-xs uppercase tracking-wide text-muted">{label}</div>
    </div>
  );
}

function StatusPill({ status }: { status: ImportPreview["rows"][number]["status"] }) {
  const map = {
    ready: { label: "Ready", cls: "bg-ok/10 text-ok" },
    duplicate: { label: "Duplicate", cls: "bg-warn/10 text-warn" },
    error: { label: "Error", cls: "bg-bad/10 text-bad" },
  } as const;
  const { label, cls } = map[status];
  return (
    <span className={cn("inline-block rounded-full px-2 py-0.5 text-xs font-semibold", cls)}>
      {label}
    </span>
  );
}
